import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgePercent, MessageCircle } from "lucide-react";
import { brand, formatIDR } from "@kanata/shared";

import { ApiError, cartApi, catalog, type ApiProduct, type UpgradePreview } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { trackOptionSelected, trackViewItem } from "@/lib/tracking";
import {
  initialSelection,
  inquirySelections,
  labelFor,
  matches,
  selectedLabel,
  type Selection,
} from "@/lib/cms-variants";
import { INQUIRY_CTA, inquiryWhatsappUrl } from "@/lib/pdp-inquiry";
import { addonsTotal, orderTotal } from "@/lib/pdp-pricing";
import { galleryForSelection } from "@/lib/product-images";
import { Button } from "@/components/ui/button";
import { Seo, SITE_URL } from "@/components/Seo";
import { PRODUCT_OG_HEIGHT, PRODUCT_OG_WIDTH, productJsonLd, productSeo } from "@/lib/product-seo";
import { PasangToggle } from "@/components/shop/PasangToggle";
import { PromoNote } from "@/components/shop/PromoNote";
import { usePromo } from "@/lib/use-promo";
import { usePasangNotice } from "@/components/shop/PasangNotice";
import { AddonList } from "@/components/product/AddonList";
import { BuyBar } from "@/components/product/BuyBar";
import { OptionSection } from "@/components/product/OptionSection";
import { PackageContents } from "@/components/product/PackageContents";
import { PriceBlock } from "@/components/product/PriceBlock";
import { ProductDetailsTabs } from "@/components/product/ProductDetailsTabs";
import { ProductGallery } from "@/components/product/ProductGallery";
import { ProductSkeleton } from "@/components/product/ProductSkeleton";
import { SizePicker } from "@/components/product/SizePicker";
import { SwatchPicker } from "@/components/product/SwatchPicker";

/**
 * The product page: gallery on the left, a sticky configurator on the right,
 * long-form content underneath.
 *
 * The configurator is a numbered walk — each option axis from the CMS, then
 * the add-ons, then installation — ending in one button that says the total.
 * Everything a customer picks is sent in one atomic add-to-cart: the server
 * either takes the whole selection or none of it.
 */
export default function Product() {
  const { slug = "" } = useParams();
  const { add, busy } = useCart();
  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selection, setSelection] = useState<Selection>({});
  const [slide, setSlide] = useState(0);
  // Off until asked for: pasang needs a technician confirmed over WhatsApp.
  const [pasang, setPasang] = useState(false);
  const [addonIds, setAddonIds] = useState<string[]>([]);
  // What the cart will do with this selection — Klasik with a Raya ticked is
  // charged as Plus. Asked of the server so the page and the cart can never
  // disagree; the à la carte total stays on screen and this sits beneath it,
  // so the customer sees both numbers and why they differ.
  const [upgradeHint, setUpgradeHint] = useState<UpgradePreview[]>([]);
  const navigate = useNavigate();
  const buyRef = useRef<HTMLDivElement>(null);
  const pasangNotice = usePasangNotice();
  const promo = usePromo();

  const selectedId = product
    ? (product.skus ?? []).find((s) => s.isActive && matches(s, selection))?.id
    : undefined;
  const addonKey = addonIds.join(",");
  useEffect(() => {
    setUpgradeHint([]);
    // Nothing to combine without something ticked beside the variant.
    if (!selectedId || addonIds.length === 0) return;
    let cancelled = false;
    const t = setTimeout(() => {
      cartApi
        .upgradePreview(selectedId, 1, addonIds)
        .then((r) => {
          if (!cancelled) setUpgradeHint(r.upgrades);
        })
        .catch(() => {
          // A hint that cannot be fetched is simply not shown; the cart
          // still applies the price.
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // addonKey stands in for addonIds, which is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, addonKey]);

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setAddonIds([]);
    setLoading(true);
    setError("");
    setSlide(0);
    catalog
      .detail(slug)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        const start = initialSelection(p);
        setSelection(start);
        const sku = (p.skus ?? []).find((s) => s.isActive && matches(s, start));
        if (sku) trackViewItem({ id: sku.code, name: p.name, priceIdr: sku.priceIdr, qty: 1 }, p.slug);
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            e instanceof ApiError && e.status === 404
              ? "Produk tidak ditemukan."
              : "Produk gagal dimuat. Silakan coba lagi.",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) return <ProductSkeleton />;

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-5 py-32 sm:px-8">
        <h1 className="text-2xl font-medium">{error || "Produk tidak ditemukan."}</h1>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/katalog">Kembali ke katalog</Link>
        </Button>
      </main>
    );
  }

  const axes = (product.axes ?? []).filter((a) => !a.notApplicable && a.valueIds.length > 0);
  const skus = product.skus ?? [];
  const selected = skus.find((s) => s.isActive && matches(s, selection));
  const gallery = galleryForSelection(
    product,
    selected,
    labelFor(axes, "top", selection),
    labelFor(axes, "leg", selection),
  );
  const addonSum = addonsTotal(product, addonIds);
  const seo = productSeo(product);
  const total = orderTotal(selected, product, addonIds);
  // Made-to-order choices have no SKU, so there is nothing to price and nothing
  // to add. The page says so and hands the shopper to WhatsApp instead.
  const inquiries = inquirySelections(axes, selection);
  const isInquiry = inquiries.length > 0;
  const summary = axes
    .map((a) => selectedLabel(a, selection))
    .filter(Boolean)
    .join(" · ");
  const inquiryHref = isInquiry
    ? inquiryWhatsappUrl(product.name, inquiries.length ? inquiries : [summary])
    : undefined;
  const canBuy =
    Boolean(selected) && !selected?.soldOut && !selected?.priceInvalid && !busy && !isInquiry;
  const buyLabel = isInquiry
    ? "Chat Admin"
    : busy
      ? "Menambahkan…"
      : selected?.soldOut
        ? "Stok habis"
        : total !== undefined
          ? `Tambah ke keranjang · ${formatIDR(total)}`
          : "Pilih varian";

  function choose(axisId: string, valueId: string) {
    const next = { ...selection, [axisId]: valueId };
    setSelection(next);
    setSlide(0);
    const axis = axes.find((a) => a.optionTypeId === axisId);
    const value = axis?.optionType.values.find((v) => v.id === valueId);
    if (!product || !axis || !value) return;
    const sku = skus.find((s) => s.isActive && matches(s, next));
    trackOptionSelected({
      productSlug: product.slug,
      optionType: axis.optionType.label,
      optionValue: value.label,
      skuCode: sku?.code,
      soldOut: sku?.soldOut,
      inquiryOnly: value.inquiryOnly,
    });
  }

  function toggleAddon(skuId: string, on: boolean) {
    setAddonIds((ids) => (on ? [...ids.filter((id) => id !== skuId), skuId] : ids.filter((id) => id !== skuId)));
  }

  async function buy() {
    if (!selected || !product) return;
    setError("");
    try {
      await add(selected.id, 1, product.pasangEligible && pasang, addonIds);
      // Straight to the cart, as the client asked: it shows what was added,
      // any Plus upgrade it made, and "Lanjut belanja" for one more thing.
      navigate("/keranjang");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal menambahkan produk");
    }
  }

  return (
    <>
      <Seo
        {...seo}
        type="product"
        imageWidth={seo.image ? PRODUCT_OG_WIDTH : undefined}
        imageHeight={seo.image ? PRODUCT_OG_HEIGHT : undefined}
        jsonLd={productJsonLd(product, SITE_URL, brand.name)}
      />
      <main className="mx-auto max-w-7xl px-5 pt-28 pb-28 sm:px-8 lg:pb-16">
        <Link to="/katalog" className="text-sm text-ink/60 transition-colors hover:text-ink">
          ← Katalog
        </Link>

        <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-start">
          <ProductGallery images={gallery} index={slide} onIndex={setSlide} name={product.name} />

          <aside className="lg:sticky lg:top-24">
            <h1 className="text-3xl font-medium tracking-tight sm:text-4xl">{product.name}</h1>
            {product.tagline && <p className="mt-2 text-ink/65">{product.tagline}</p>}

            <PriceBlock
              selected={selected}
              addonsTotal={addonSum}
              total={total}
              isInquiry={isInquiry}
              className="mt-5"
            />
            {upgradeHint.map((u) => (
              <p
                key={u.skuId}
                role="status"
                className="mt-3 flex items-start gap-2 rounded-xl bg-brand-light/60 px-4 py-3 text-sm text-brand-dark"
              >
                <BadgePercent className="mt-0.5 size-4 shrink-0" />
                {u.notice}
              </p>
            ))}
            {promo && product.line === "desk" && <PromoNote promo={promo} className="mt-4" />}
            {selected?.isPackage && <PackageContents sku={selected} className="mt-5" />}

            <div className="mt-7 space-y-7">
              {axes.map((a, i) => {
                const headingId = `axis-${a.optionTypeId}`;
                return (
                  <OptionSection
                    key={a.optionTypeId}
                    n={i + 1}
                    id={headingId}
                    title={a.optionType.label}
                    selectedLabel={selectedLabel(a, selection)}
                  >
                    {a.optionType.control === "swatch" ? (
                      <SwatchPicker
                        axis={a}
                        skus={skus}
                        selection={selection}
                        onChange={(v) => choose(a.optionTypeId, v)}
                        labelledBy={headingId}
                      />
                    ) : (
                      <SizePicker
                        axis={a}
                        skus={skus}
                        selection={selection}
                        onChange={(v) => choose(a.optionTypeId, v)}
                        labelledBy={headingId}
                      />
                    )}
                  </OptionSection>
                );
              })}

              {!!product.addons?.length && (
                <AddonList
                  n={axes.length + 1}
                  addons={product.addons}
                  selectedIds={addonIds}
                  onToggle={toggleAddon}
                  disabled={busy}
                />
              )}
            </div>

            {product.pasangEligible && (
              <PasangToggle
                className="mt-7"
                checked={pasang}
                onChange={(on) => {
                  setPasang(on);
                  if (on) pasangNotice.show({ productNames: [product.name] });
                }}
                disabled={busy}
              />
            )}

            <div ref={buyRef} className="mt-7">
              {isInquiry ? (
                <>
                  <p className="mb-3 text-sm text-ink/70">{INQUIRY_CTA}.</p>
                  <Button size="lg" className="w-full" asChild>
                    <a href={inquiryHref} target="_blank" rel="noopener noreferrer" data-wa-placement="product_inquiry" data-product-slug={product.slug}>
                      <MessageCircle />
                      {buyLabel}
                    </a>
                  </Button>
                </>
              ) : (
                <Button variant="cart" size="lg" className="w-full" disabled={!canBuy} onClick={() => void buy()}>
                  {buyLabel}
                </Button>
              )}
              {error && (
                <p role="alert" className="mt-3 text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>
          </aside>
        </div>

        <ProductDetailsTabs
          description={product.description}
          specs={product.specs ?? []}
          className="mt-16"
        />
      </main>

      <BuyBar
        anchor={buyRef}
        observeKey={product.id}
        name={product.name}
        summary={summary || undefined}
        label={buyLabel}
        disabled={!canBuy}
        onBuy={() => void buy()}
        href={inquiryHref}
      />
      {pasangNotice.dialog}
    </>
  );
}
